import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useKeyboardContext } from '@/core/keyboard';
import { usePosBilling } from '@/pages/pos/PosBillingContext';

export function PosCartPane() {
  const { setFocusZone, setStatusHint } = useKeyboardContext();
  const {
    cart,
    totals,
    selectedIndex,
    setSelectedIndex,
    updateQty,
    fmt,
    setQtyEdit,
  } = usePosBilling();

  const onCartKeyDown = (e: React.KeyboardEvent) => {
    if (!cart.items.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, cart.items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = cart.items[selectedIndex];
      if (item) setQtyEdit(String(item.quantity));
    } else if (e.key === '+' || e.key === '=') {
      e.preventDefault();
      const item = cart.items[selectedIndex];
      if (item) updateQty(selectedIndex, item.quantity + 1);
    } else if (e.key === '-') {
      e.preventDefault();
      const item = cart.items[selectedIndex];
      if (item && item.quantity > 1) updateQty(selectedIndex, item.quantity - 1);
    }
  };

  return (
    <section
      className="flex min-h-0 flex-1 flex-col outline-none"
      tabIndex={0}
      onKeyDown={onCartKeyDown}
      onFocus={() => {
        setFocusZone('cart');
        setStatusHint('↑↓ lines · +/- qty · Enter edit · Del remove');
      }}
      aria-label="Cart"
    >
      <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2 dark:border-slate-700">
        <h2 className="text-sm font-bold uppercase tracking-wide text-slate-700 dark:text-slate-300">
          Cart
        </h2>
        <span className="text-xs text-slate-500">
          {cart.items.length} item{cart.items.length !== 1 ? 's' : ''}
        </span>
      </div>
      <div className="flex-1 overflow-y-auto p-2">
        {cart.items.length === 0 ? (
          <p className="py-12 text-center text-sm text-slate-500">Cart empty — search and add products</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 text-xs uppercase text-slate-500 dark:bg-slate-900">
              <tr>
                <th className="py-1 text-left">Item</th>
                <th className="py-1 text-center w-16">Qty</th>
                <th className="py-1 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {cart.items.map((item, i) => (
                <tr
                  key={`${item.productId}-${i}`}
                  onClick={() => setSelectedIndex(i)}
                  className={`cursor-pointer border-b border-slate-100 dark:border-slate-800 ${
                    i === selectedIndex
                      ? 'bg-primary-100/80 dark:bg-primary-900/30'
                      : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
                  }`}
                >
                  <td className="py-2 pr-2">
                    <p className="font-medium text-slate-900 dark:text-white">{item.name}</p>
                    <p className="text-xs text-slate-500">
                      {fmt(item.unitPrice)} × {item.quantity}
                    </p>
                  </td>
                  <td className="py-2 text-center">
                    <div className="inline-flex items-center gap-0.5 rounded border border-slate-200 dark:border-slate-600">
                      <button
                        type="button"
                        tabIndex={-1}
                        className="p-0.5"
                        onClick={(e) => {
                          e.stopPropagation();
                          updateQty(i, Math.max(1, item.quantity - 1));
                        }}
                      >
                        <RemoveIcon sx={{ fontSize: 16 }} />
                      </button>
                      <span className="min-w-[2ch] font-semibold">{item.quantity}</span>
                      <button
                        type="button"
                        tabIndex={-1}
                        className="p-0.5"
                        onClick={(e) => {
                          e.stopPropagation();
                          updateQty(i, item.quantity + 1);
                        }}
                      >
                        <AddIcon sx={{ fontSize: 16 }} />
                      </button>
                    </div>
                  </td>
                  <td className="py-2 text-right font-semibold">{fmt(item.lineTotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="border-t border-slate-200 bg-slate-50 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900">
        <div className="flex justify-between text-slate-600 dark:text-slate-400">
          <span>Subtotal</span>
          <span>{fmt(totals.subtotal)}</span>
        </div>
        <div className="flex justify-between text-slate-600 dark:text-slate-400">
          <span>Discount</span>
          <span>-{fmt(cart.discountAmount)}</span>
        </div>
        <div className="mt-1 flex justify-between text-xl font-bold text-slate-900 dark:text-white">
          <span>Total</span>
          <span>{fmt(totals.total)}</span>
        </div>
        <p className="mt-1 text-[10px] text-slate-500">↑↓ lines · +/- qty · Delete removes line</p>
      </div>
    </section>
  );
}
